/**
 * ============================================================================
 * UPDATE PASSWORD - Change Password for Logged-in Users
 * ============================================================================
 * Purpose: Allow authenticated users to change their password
 * 
 * BACKEND INTEGRATION POINTS:
 * ----------------------------------------------------------------------------
 * 🔗 Backend: POST /api/v1/auth/update-password
 * Purpose: Update password for logged-in user
 * Auth: Requires valid accessToken (Bearer token in Authorization header)
 * Request Body: { oldPassword: string, newPassword: string }
 * Success Response: { statusCode: 200, message: "Password updated successfully" }
 * Error Responses:
 *   - 400: Old password is incorrect
 *   - 400: New password validation failed
 *   - 401: Unauthorized (invalid/expired token)
 * ============================================================================
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';

const UpdatePassword = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage({ type: '', text: '' });

    // Validation
    const newErrors = {};
    
    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = 'Current password is required';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else {
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (formData.oldPassword === formData.newPassword && formData.oldPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const API_URL = 'https://taskscheduler-backend.onrender.com/api/v1/auth/change-password';
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        })
      });

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        setMessage({
          type: 'success',
          text: 'Password updated successfully!'
        });
        setFormData({ oldPassword: '', newPassword: '' });
        
        // Optionally redirect to dashboard after success
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else if (result.statusCode === 400) {
        if (result.message.toLowerCase().includes('old password')) {
          setErrors({ oldPassword: 'Current password is incorrect' });
        } else {
          setMessage({
            type: 'error',
            text: result.message || 'Failed to update password'
          });
        }
      } else if (result.statusCode === 401) {
        setMessage({
          type: 'error',
          text: 'Session expired. Please log in again.'
        });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage({
          type: 'error',
          text: result.message || 'Failed to update password. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({
        type: 'error',
        text: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cardBg = isDarkMode
    ? 'linear-gradient(145deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)'
    : 'linear-gradient(145deg, #ffffff 0%, rgba(249, 250, 251, 1) 100%)';

  const inputBg = isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff';
  const inputBorder = isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(209, 213, 219, 0.5)';
  const textColor = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#111827';
  const textSecondary = isDarkMode ? 'rgba(156, 163, 175, 0.9)' : '#6b7280';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        background: isDarkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: cardBg,
          borderRadius: '1.5rem',
          boxShadow: isDarkMode
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          padding: window.innerWidth <= 480 ? '2rem 1.5rem' : '3rem 2.5rem',
          border: isDarkMode ? '1px solid rgba(75, 85, 99, 0.2)' : '1px solid rgba(229, 231, 235, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div
            style={{
              width: '4rem',
              height: '4rem',
              borderRadius: '1.25rem',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.2) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              border: '2px solid rgba(139, 92, 246, 0.3)'
            }}
          >
            <i className="fas fa-shield-alt" style={{ fontSize: '1.75rem', color: '#8b5cf6' }}></i>
          </div>
          <h1
            style={{
              fontSize: window.innerWidth <= 480 ? '1.75rem' : '2rem',
              fontWeight: 800,
              color: textColor,
              marginBottom: '0.5rem'
            }}
          >
            Change Password
          </h1>
          <p style={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6 }}>
            Update your password to keep your account secure.
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            style={{
              padding: '1rem 1.25rem',
              borderRadius: '0.85rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: message.type === 'success'
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.15) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.15) 100%)',
              border: message.type === 'success'
                ? '1px solid rgba(34, 197, 94, 0.3)'
                : '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <i
              className={message.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}
              style={{
                fontSize: '1.25rem',
                color: message.type === 'success' ? '#22c55e' : '#ef4444'
              }}
            ></i>
            <span style={{ color: message.type === 'success' ? '#22c55e' : '#ef4444', fontSize: '0.9rem', fontWeight: 600 }}>
              {message.text}
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Old Password Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="oldPassword"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: textColor,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              Current Password
            </label>
            <div style={{ position: 'relative', minHeight: '52px' }}>
              <input
                type="password"
                id="oldPassword"
                name="oldPassword"
                value={formData.oldPassword}
                onChange={handleChange}
                placeholder="Enter current password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 1.25rem',
                  borderRadius: '0.85rem',
                  border: errors.oldPassword
                    ? '2px solid #ef4444'
                    : `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: textColor,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isLoading ? 'not-allowed' : 'text',
                  opacity: isLoading ? 0.6 : 1
                }}
              />
            </div>
            {errors.oldPassword && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fas fa-exclamation-circle"></i>
                {errors.oldPassword}
              </p>
            )}
          </div>

          {/* New Password Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="newPassword"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: textColor,
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              New Password
            </label>
            <div style={{ position: 'relative', minHeight: '52px' }}>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 1.25rem',
                  borderRadius: '0.85rem',
                  border: errors.newPassword
                    ? '2px solid #ef4444'
                    : `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: textColor,
                  fontSize: '0.95rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  cursor: isLoading ? 'not-allowed' : 'text',
                  opacity: isLoading ? 0.6 : 1
                }}
              />
            </div>
            {errors.newPassword && (
              <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fas fa-exclamation-circle"></i>
                {errors.newPassword}
              </p>
            )}
            
            {/* Password Requirements */}
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: isDarkMode ? 'rgba(31, 41, 55, 0.3)' : 'rgba(243, 244, 246, 0.5)',
                borderRadius: '0.5rem',
                fontSize: '0.8rem'
              }}
            >
              <p style={{ color: textSecondary, marginBottom: '0.5rem', fontWeight: 600 }}>New password must contain:</p>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: textSecondary, lineHeight: 1.8 }}>
                <li>At least 8 characters</li>
                <li>One uppercase letter</li>
                <li>One lowercase letter</li>
                <li>One number</li>
              </ul>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '1rem',
              borderRadius: '0.85rem',
              border: 'none',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#ffffff',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 35px rgba(139, 92, 246, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(139, 92, 246, 0.3)';
            }}
          >
            {isLoading ? (
              <Loader variant="inline" size="sm" text="Updating..." isDarkMode={isDarkMode} />
            ) : (
              <>
                <i className="fas fa-check"></i>
                Update Password
              </>
            )}
          </button>
        </form>

        {/* Back to Dashboard */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b5cf6',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.gap = '0.75rem';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.gap = '0.5rem';
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
